import { ComponentType, Suspense, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect, Route } from "react-router-dom";
import Spinner from "../Component/LoadingSpiner";
import Api from "../Network";
import { deleteQueryPresister } from "../Utilis/createQueryPresister";
import { requestManager } from "../utils/requestManager";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "../Component/ErrorFallback/ErrorFallback";

export interface RouteProps {
  component: ComponentType<any>;
  isAuthenticated: boolean;
  [key: string]: any;
  exact?: boolean;
}

export const PrivateRoute: React.FC<RouteProps> = ({
  component: Component,
  isAuthenticated,
  exact,
  ...rest
}) => {
  const { userInfo } = useSelector((state: any) => state.setReducer);
  const [shouldRedirectToLogin, setShouldRedirectToLogin] = useState(false);

  function handleLogout() {
    // Set flags BEFORE any API call so interceptors stay fully silent.
    localStorage.setItem("isLoggingOut", "true");
    localStorage.setItem("silentLogout", "true");

    const companyId = userInfo?.company_instance?._id;
    Api.post("auth/logout", { company: companyId }).catch(() => {
      // Fire-and-forget: local logout always proceeds.
    });

    try {
      requestManager.cancelAllRequests();
    } catch {
      // noop
    }

    // Local cleanup only — do NOT dispatch setUserInfo(null) here. That triggers PrivateRoute
    // to emit a <Redirect to="/login" /> in the same tick as window.location.href below,
    // which produces a visible double navigation. The full page load wipes state on its own.
    localStorage.removeItem("token");
    localStorage.removeItem("socketUrl");
    localStorage.removeItem("refresh-token");
    deleteQueryPresister();

    // Login page will clear the logout flags on mount.
    window.location.href = "/login";
  }

  // Token + user bootstrap is handled in App.tsx (auth/me + company). Do not POST auth/me here —
  // a second in-flight request was cancelling the first via requestManager and broke login.
  useEffect(() => {
    if (userInfo) {
      setShouldRedirectToLogin(false);
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) {
      setShouldRedirectToLogin(true);
    }
  }, [userInfo]);

  if (shouldRedirectToLogin) {
    return <Redirect to="/login" />;
  }

  if (!userInfo) {
    return <Spinner />;
  }

  if (!isAuthenticated) {
    handleLogout();
    return <Redirect to="/" />;
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Route
        {...rest}
        exact={exact}
        render={(props) =>
          true ? (
            <Suspense fallback={<Spinner />}>
              <Component {...props} />
            </Suspense>
          ) : (
            <Redirect to="/login" />
          )
        }
      />
    </ErrorBoundary>
  );
};

export const PublicRoute: React.FC<RouteProps> = ({
  component: Component,
  isAuthenticated,
  ...rest
}) => <Route {...rest} exact render={(props) => <Component {...props} />} />;
